import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, ArrowRight } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { listMyFirms, createFirm, listOpenFirmAssignments } from "@/lib/api/lxp-firms.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/firms/")({
  head: () => ({ meta: [{ title: "Law Firm Portal | SA LexPractiq" }] }),
  component: FirmsPage,
});

function FirmsPage() {
  const listMineFn = useServerFn(listMyFirms);
  const createFn = useServerFn(createFirm);
  const openAssignFn = useServerFn(listOpenFirmAssignments);
  const [firms, setFirms] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState(""); const [desc, setDesc] = useState(""); const [site, setSite] = useState("");

  async function reload() {
    const [a, b] = await Promise.all([listMineFn(), openAssignFn()]);
    setFirms(a.firms); setAssignments(b.assignments);
  }
  useEffect(() => { reload(); }, []);

  async function create() {
    if (name.length < 2) return toast.error("Firm name required");
    try {
      await createFn({ data: { name, description: desc || undefined, website: site || undefined, practiceAreas: [] } });
      toast.success("Firm workspace created");
      setName(""); setDesc(""); setSite(""); setShowForm(false);
      await reload();
    } catch (e: any) { toast.error(e?.message); }
  }

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
          <div>
            <span className="lxp-badge"><Building2 size={12} /> Law Firm Portal</span>
            <h1 style={{ margin: "0.5rem 0 0.25rem" }}>Firm workspaces</h1>
            <p className="lxp-muted" style={{ margin: 0 }}>Publish practical assignments, review candidate submissions, and give structured feedback.</p>
          </div>
          <button className="lxp-btn" onClick={() => setShowForm((s) => !s)}><Plus size={14} /> New workspace</button>
        </div>

        {showForm && (
          <div className="lxp-card" style={{ marginBottom: "1.25rem" }}>
            <h3 style={{ margin: "0 0 0.5rem" }}>Create firm workspace</h3>
            <div style={{ display: "grid", gap: "0.5rem" }}>
              <input className="lxp-input" placeholder="Firm name" value={name} onChange={(e) => setName(e.target.value)} />
              <textarea className="lxp-textarea" rows={3} placeholder="Short description" value={desc} onChange={(e) => setDesc(e.target.value)} />
              <input className="lxp-input" placeholder="https://firm.com" value={site} onChange={(e) => setSite(e.target.value)} />
              <div><button className="lxp-btn" onClick={create}>Create</button></div>
            </div>
          </div>
        )}

        <h3>My firm workspaces</h3>
        {firms.length === 0 && <p className="lxp-muted">You don't belong to any firm workspace yet.</p>}
        <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
          {firms.map((f: any) => (
            <Link key={f.id} to="/lexpractiq/firms/$firmId" params={{ firmId: f.id }} className="lxp-card" style={{ textDecoration: "none", display: "flex", justifyContent: "space-between" }}>
              <div>
                <strong>{f.name}</strong>
                <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>{f.member_role}</div>
              </div>
              <ArrowRight size={16} />
            </Link>
          ))}
        </div>

        <h3 style={{ marginTop: "1.5rem" }}>Open firm assignments — apply your skills</h3>
        <div style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          {assignments.map((a: any) => (
            <Link key={a.id} to="/lexpractiq/firms/assignment/$id" params={{ id: a.id }} className="lxp-card" style={{ textDecoration: "none" }}>
              {a.firm?.name && <span className="lxp-badge">{a.firm.name}</span>}
              <h4 style={{ margin: "0.4rem 0 0.25rem", fontSize: "0.95rem" }}>{a.title}</h4>
              {a.practice_area && <div className="lxp-muted" style={{ fontSize: "0.8rem" }}>{a.practice_area}</div>}
              {a.deadline && <div className="lxp-muted" style={{ fontSize: "0.75rem", marginTop: 4 }}>Due {new Date(a.deadline).toLocaleDateString()}</div>}
            </Link>
          ))}
          {assignments.length === 0 && <p className="lxp-muted">No open assignments right now.</p>}
        </div>
      </div>
    </LxpLayout>
  );
}
